import React from 'react';
import { Autocomplete, Box, CircularProgress, Grid, InputAdornment, TextField } from '@mui/material';
import { Building2, ChevronDown, Globe, MapPin, Search } from 'lucide-react';
import CustomValidationText from 'ui-components/CustomValidationText';
import useLocationSearch from 'hooks/useLocationSearch';
import { autocompleteSlotSx, FieldIcon, FieldLabel, INDIGO } from '../primitives';

/**
 * Investor location as one searchable field, like the startup app's: picking a place
 * fills whichever of city / state / country it has. "Enter it manually" keeps the old
 * Country -> State -> City cascade. Used by onboarding and profile editing.
 * Render inside a <Grid container>.
 */
const LocationFields = ({ formik }) => {
  const {
    matches,
    isSearching,
    searchable,
    selectOption,
    selectedOption,
    manualEntry,
    enterManual,
    exitManual,
    countries,
    states,
    cities,
    isLoadingCountries,
    isLoadingStates,
    isLoadingCities,
    savedCountry,
    savedState,
    savedCity,
    showStateField,
    showCityField,
    onSearchInput,
    selectCountry,
    selectState,
    selectCity,
  } = useLocationSearch(formik);

  const errors = formik.errors || {};

  const adornment = (icon) => (
    <InputAdornment position="start">
      <FieldIcon icon={icon} size={18} />
    </InputAdornment>
  );

  // The mode link sits on the label row, so it reads as part of this field.
  const labelRow = (label, required, link) => (
    <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap', mb: '8px' }}>
      <FieldLabel required={required} sx={{ mb: 0 }}>{label}</FieldLabel>
      {link}
    </Box>
  );

  const modeLink = (label, onClick) => (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      sx={{
        padding: 0,
        border: 'none',
        background: 'none',
        color: INDIGO,
        cursor: 'pointer',
        fontSize: '0.75rem',
        fontWeight: 500,
        lineHeight: 1.4,
        '&:hover': { textDecoration: 'underline' },
      }}
    >
      {label}
    </Box>
  );

  const slotInput = (params, { placeholder, icon, error, endAdornment }) => (
    <TextField
      {...params}
      variant="filled"
      placeholder={placeholder}
      error={!!error}
      sx={autocompleteSlotSx}
      InputProps={{
        ...params.InputProps,
        disableUnderline: true,
        startAdornment: adornment(icon),
        ...(endAdornment !== undefined && { endAdornment }),
      }}
    />
  );

  if (!manualEntry) {
    return (
      <Grid item xs={12}>
        {labelRow('Location', true, modeLink("Can't find your place? Enter it manually", enterManual))}
        <Autocomplete
          options={matches}
          // The server already matched and ranked the term.
          filterOptions={(options) => options}
          getOptionLabel={(option) => option?.label || ''}
          isOptionEqualToValue={(option, other) => option?.label === other?.label}
          value={selectedOption}
          loading={isSearching}
          onInputChange={(event, next, reason) => {
            // Picking an option also fires this; only real typing searches.
            if (reason === 'input') onSearchInput(next);
          }}
          onChange={(event, option) => selectOption(option)}
          noOptionsText={searchable ? 'No places found' : 'Type at least two letters'}
          popupIcon={null}
          renderInput={(params) =>
            slotInput(params, {
              placeholder: 'City, state or country',
              icon: Search,
              error: errors.country_id,
              endAdornment: isSearching
                ? <CircularProgress size={16} sx={{ marginRight: '12px' }} />
                : params.InputProps?.endAdornment,
            })
          }
        />
        {errors.country_id && <CustomValidationText value={errors.country_id} />}
      </Grid>
    );
  }

  // Manual entry: the original cascade. Country is required; state and city as far as known.
  const span = 12 / (1 + showStateField + showCityField);

  return (
    <>
      <Grid item xs={12} md={span}>
        {labelRow('Country', true, modeLink('Search for a place instead', exitManual))}
        <Autocomplete
          options={countries || []}
          loading={isLoadingCountries}
          getOptionLabel={(option) => option?.label || ''}
          isOptionEqualToValue={(option, other) => option?._id === other?._id}
          value={savedCountry || null}
          onChange={(event, option) => selectCountry(option)}
          noOptionsText="No countries"
          popupIcon={<ChevronDown size={18} color="#667085" />}
          renderInput={(params) => slotInput(params, { placeholder: 'Select country', icon: Globe, error: errors.country_id })}
        />
        {errors.country_id && <CustomValidationText value={errors.country_id} />}
      </Grid>

      {showStateField && (
        <Grid item xs={12} md={span}>
          {labelRow('State / Province', false, null)}
          <Autocomplete
            options={states || []}
            loading={isLoadingStates}
            getOptionLabel={(option) => option?.label || ''}
            isOptionEqualToValue={(option, other) => option?._id === other?._id}
            value={savedState || null}
            onChange={(event, option) => selectState(option)}
            disabled={!formik.values.country_id}
            noOptionsText="No states"
            popupIcon={<ChevronDown size={18} color="#667085" />}
            renderInput={(params) => slotInput(params, { placeholder: 'Select state', icon: MapPin, error: errors.state_id })}
          />
          {errors.state_id && <CustomValidationText value={errors.state_id} />}
        </Grid>
      )}

      {showCityField && (
        <Grid item xs={12} md={span}>
          {labelRow('City', false, null)}
          <Autocomplete
            options={cities || []}
            loading={isLoadingCities}
            getOptionLabel={(option) => option?.label || ''}
            isOptionEqualToValue={(option, other) => option?._id === other?._id}
            value={savedCity || null}
            onChange={(event, option) => selectCity(option)}
            disabled={!formik.values.state_id}
            noOptionsText="No cities"
            popupIcon={<ChevronDown size={18} color="#667085" />}
            renderInput={(params) => slotInput(params, { placeholder: 'Select city', icon: Building2, error: errors.city_id })}
          />
          {errors.city_id && <CustomValidationText value={errors.city_id} />}
        </Grid>
      )}
    </>
  );
};

export default LocationFields;
